/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * SectionPalette — left sidebar with two tabs:
 *   Sections — all built-in section types grouped by category
 *   Templates — org/global saved section presets
 */

import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../theme/ThemeContext';
import { useSectionTemplates, deleteTemplate } from '../../hooks/usePages';
import TemplatePreviewModal from './TemplatePreviewModal';
import { useAlertDialog } from '../common/AlertDialogContext';

const SECTIONS = [
  {
    group: 'Layout',
    items: [
      { type: 'hero',         icon: 'bi-image',          label: 'Hero' },
      { type: 'text_block',   icon: 'bi-justify-left',   label: 'Text Block' },
      { type: 'image_banner', icon: 'bi-card-image',     label: 'Image Banner' },
      { type: 'stat_widgets', icon: 'bi-bar-chart-line', label: 'Stat Widgets' },
    ],
  },
  {
    group: 'Data',
    items: [
      { type: 'data_cards',    icon: 'bi-grid',            label: 'Data Cards',  hidden: true },
      { type: 'data_table',    icon: 'bi-table',           label: 'Data Table',  hidden: true },
      { type: 'data_list',     icon: 'bi-list-ul',         label: 'Data List',   hidden: true },
      { type: 'data_timeline', icon: 'bi-clock-history',   label: 'Timeline',    hidden: true },
      { type: 'chart_bar',     icon: 'bi-bar-chart',       label: 'Bar Chart',   hidden: true },
      { type: 'chart_line',    icon: 'bi-graph-up',        label: 'Line Chart',  hidden: true },
    ],
  },
  {
    group: 'Forms',
    items: [
      { type: 'form_builder', icon: 'bi-ui-checks', label: 'Form Builder' },
    ],
  },
  {
    group: 'Public Pages',
    items: [
      { type: 'public_page', icon: 'bi-window-stack', label: 'Public Page' },
    ],
  },
  {
    group: 'Elements',
    items: [
      { type: 'divider',            icon: 'bi-hr',         label: 'Divider' },
      { type: 'html_css_workspace', icon: 'bi-code-slash', label: 'HTML / CSS / JS' },
      { type: 'component',          icon: 'bi-puzzle',     label: 'Component' },
    ],
  },
];

export { SECTIONS };

export default function SectionPalette({ appId, onAdd, onAddTemplate, onAddPageTemplate }) {
  const { theme } = useTheme();
  const { showAlertDialog } = useAlertDialog();
  const queryClient = useQueryClient();
  const [tab, setTab]           = useState('sections');
  const [tplGroup, setTplGroup] = useState('section'); // 'section' | 'page'
  const [deletingId, setDeletingId]           = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const { data: templates = [], isLoading: templatesLoading } = useSectionTemplates(appId);

  const sectionTemplates = templates.filter(t => t.section_type !== 'page_template');
  const pageTemplates    = templates.filter(t => t.section_type === 'page_template');

  const handleDeleteTemplate = (e, tpl) => {
    e.stopPropagation();
    showAlertDialog({
      title: 'Delete Template',
      message: `Are you sure you want to delete "${tpl.name}"? This cannot be undone.`,
      type: 'warning',
      okLabel: 'Delete',
      cancelLabel: 'Cancel',
      showCancel: true,
      onOk: async () => {
        setDeletingId(tpl.id);
        try {
          await deleteTemplate(tpl.id);
          queryClient.invalidateQueries({ queryKey: ['sectionTemplates', appId] });
        } catch (_) {
          // non-critical
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const tabStyle = (key) => ({
    flex: 1,
    padding: '7px 0',
    border: 'none',
    borderBottom: tab === key ? `2px solid ${theme.primary}` : '2px solid transparent',
    background: 'transparent',
    color: tab === key ? theme.primary : '#64748b',
    fontWeight: tab === key ? 700 : 400,
    fontSize: 11,
    cursor: 'pointer',
  });

  return (
    <div
      style={{
        background: '#fff',
        borderRight: '1px solid #e2e8f0',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Tab bar */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
          flexShrink: 0,
        }}
      >
        <button style={tabStyle('sections')} onClick={() => setTab('sections')}>Sections</button>
        <button style={tabStyle('templates')} onClick={() => setTab('templates')}>Templates</button>
      </div>

      {/* Sections tab */}
      {tab === 'sections' && (
        <div>
          {SECTIONS.map(({ group, items }) => {
            const visibleItems = items.filter(item => !item.hidden);
            if (visibleItems.length === 0) return null;
            return (
              <div key={group}>
                <div
                  className="px-3 pt-3 pb-1"
                  style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em' }}
                >
                  {group.toUpperCase()}
                </div>
                {visibleItems.map(({ type, icon, label }) => (
                  <PaletteTile
                    key={type}
                    type={type}
                    icon={icon}
                    label={label}
                    theme={theme}
                    onClick={() => onAdd(type)}
                  />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Template preview modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}


      {/* Templates tab */}
      {tab === 'templates' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

          {/* Sub-tab selector */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
            {[
              { key: 'section', icon: 'bi-bookmark',           label: 'Section', count: sectionTemplates.length },
              { key: 'page',    icon: 'bi-file-earmark-ruled', label: 'Page',    count: pageTemplates.length    },
            ].map(g => (
              <button
                key={g.key}
                type="button"
                onClick={() => setTplGroup(g.key)}
                style={{
                  flex: 1,
                  padding: '7px 4px',
                  border: 'none',
                  borderBottom: tplGroup === g.key ? `2px solid ${theme.primary}` : '2px solid transparent',
                  background: 'transparent',
                  color: tplGroup === g.key ? theme.primary : '#64748b',
                  fontWeight: tplGroup === g.key ? 700 : 400,
                  fontSize: 11,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                }}
              >
                <i className={`bi ${g.icon}`} style={{ fontSize: 11 }} />
                {g.label}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '1px 5px',
                    borderRadius: 10,
                    background: tplGroup === g.key ? theme.primary + '20' : '#f1f5f9',
                    color: tplGroup === g.key ? theme.primary : '#94a3b8',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {g.count}
                </span>
              </button>
            ))}
          </div>

          {/* Template list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {templatesLoading ? (
              <div className="text-center py-4">
                <span className="spinner-border spinner-border-sm" style={{ color: theme.primary }} />
              </div>
            ) : tplGroup === 'section' ? (
              sectionTemplates.length === 0 ? (
                <div style={{ padding: '20px 12px', textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>
                  <i className="bi bi-bookmark" style={{ fontSize: 26, display: 'block', marginBottom: 8, opacity: 0.35 }} />
                  No section templates yet.
                  <br />Open any section's Edit panel and click<br />"Save Section as Template".
                </div>
              ) : (
                sectionTemplates.map(tpl => (
                  <TemplateRow
                    key={tpl.id}
                    tpl={tpl}
                    icon="bi-bookmark-fill"
                    subLabel={tpl.section_type?.replace(/_/g, ' ')}
                    theme={theme}
                    deletingId={deletingId}
                    onUse={() => onAddTemplate && onAddTemplate(tpl)}
                    onPreview={() => setPreviewTemplate(tpl)}
                    onDelete={(e) => handleDeleteTemplate(e, tpl)}
                  />
                ))
              )
            ) : (
              pageTemplates.length === 0 ? (
                <div style={{ padding: '20px 12px', textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>
                  <i className="bi bi-file-earmark-ruled" style={{ fontSize: 26, display: 'block', marginBottom: 8, opacity: 0.35 }} />
                  No page templates yet.
                  <br />Click "Page Template" in the toolbar<br />to save the current page layout.
                </div>
              ) : (
                pageTemplates.map(tpl => (
                  <TemplateRow
                    key={tpl.id}
                    tpl={tpl}
                    icon="bi-file-earmark-ruled"
                    subLabel={`${tpl.default_config?.sections?.length ?? 0} sections`}
                    theme={theme}
                    deletingId={deletingId}
                    onUse={() => onAddPageTemplate && onAddPageTemplate(tpl)}
                    onPreview={null}
                    onDelete={(e) => handleDeleteTemplate(e, tpl)}
                  />
                ))
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateRow({ tpl, icon, subLabel, theme, deletingId, onUse, onPreview, onDelete }) {
  return (
    <div
      onClick={onUse}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 8px 7px 12px',
        cursor: 'pointer',
        borderRadius: 6,
        margin: '2px 6px',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = theme.primary + '15')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      title={`Use template: ${tpl.name}`}
    >
      <i
        className={`bi ${icon}`}
        style={{ fontSize: 14, color: theme.primary, width: 18, flexShrink: 0, textAlign: 'center' }}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tpl.name}
        </div>
        <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'capitalize' }}>
          {subLabel}
        </div>
      </div>
      {onPreview && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPreview(); }}
          title="Preview template"
          style={{ flexShrink: 0, background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', color: '#94a3b8', borderRadius: 4, lineHeight: 1, fontSize: 12 }}
          onMouseEnter={e => (e.currentTarget.style.color = theme.primary)}
          onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
        >
          <i className="bi bi-eye" />
        </button>
      )}
      <button
        type="button"
        onClick={onDelete}
        disabled={deletingId === tpl.id}
        title="Delete template"
        style={{ flexShrink: 0, background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', color: '#94a3b8', borderRadius: 4, lineHeight: 1, fontSize: 12 }}
        onMouseEnter={e => (e.currentTarget.style.color = '#dc2626')}
        onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
      >
        {deletingId === tpl.id
          ? <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} />
          : <i className="bi bi-trash" />
        }
      </button>
    </div>
  );
}

function PaletteTile({ type, icon, label, theme, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 12px',
        cursor: 'pointer',
        borderRadius: 6,
        margin: '2px 6px',
        transition: 'background 0.12s',
        userSelect: 'none',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = theme.primary + '15')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      title={`Add ${label} section`}
    >
      <i
        className={`bi ${icon}`}
        style={{ fontSize: 14, color: theme.primary, width: 18, textAlign: 'center' }}
      />
      <span style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>{label}</span>
    </div>
  );
}
